import * as React from 'react';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { ProjectTypeGq } from '../../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { GetProjectsCommandGq } from '../../../graphql/projects/queries/useGetProjectsGq';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import VoxelAutocomplete, { GenericOption } from '../../voxel/Forms/VoxelAutocomplete';

export interface ProjectAutocompleteProps {
  project: ProjectGq;
  selectedProject: Partial<ProjectGq> | null;
  setSelectedProject: Dispatch<SetStateAction<Partial<ProjectGq> | null>>;
  setSelectedTagGroups: Dispatch<SetStateAction<number[]>>;
  disabled: boolean;
  types?: ProjectTypeGq[];
}

const ProjectAutocomplete: React.FC<ProjectAutocompleteProps> = ({
  project,
  selectedProject,
  setSelectedProject,
  setSelectedTagGroups,
  disabled,
  types = [ProjectTypeGq.NORMAL_SEARCH],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState<GetProjectsCommandGq>({
    userOnly: false,
    showArchived: false,
    searchTerm: '',
    sortBy: 'createTime',
    sortDirection: 'asc',
    page: 0,
    amountPerPage: 15,
    types,
  });
  useEffect(
    () =>
      setFilterState((prev) => (prev.searchTerm === searchTerm ? prev : { ...prev, searchTerm })),
    [searchTerm]
  );
  const debouncedSearchTerm = useDebounceValue(filterState.searchTerm, 300);
  const projectsQuery = projectsApiService.queries.useGetProjects({
    ...filterState,
    searchTerm: debouncedSearchTerm, // We don't want the query to refresh several times per second (e.g. typing speed)
    currentProjectId: project.id,
  });

  const projectList: ProjectGq[] = useMemo(
    () => projectsQuery?.data?.projects ?? [],
    [projectsQuery.data?.projects]
  );

  const importProject = projectsApiService.queries.useGetProject({
    tagImportProject: true,
  })?.result;

  const selectedOption: GenericOption = useMemo(() => {
    if (selectedProject) {
      return {
        title: selectedProject.name,
        subtitle: selectedProject.createdBy.team.name,
        value: selectedProject.id,
      };
    } else return null;
  }, [selectedProject]);

  const options = useMemo(() => {
    const base_options: GenericOption[] = [];
    if (!debouncedSearchTerm?.length && importProject) {
      base_options.push({
        value: importProject.id,
        title: importProject.name,
        subtitle: importProject.createdBy.team.name,
      });
    }
    projectsQuery?.data?.projects?.forEach((x) =>
      base_options.push({
        value: x.id,
        title: x.name,
        subtitle: x.createdBy.team.name,
      })
    );
    return base_options;
  }, [importProject, projectsQuery.data?.projects]);

  const selectedOptionsValues = useMemo(
    () => (selectedOption ? [selectedOption?.value] : []),
    [selectedOption]
  );

  return (
    <VoxelAutocomplete
      label="Project"
      placeholder={'Select project'}
      multi={false}
      backendSearchTerm={searchTerm}
      setBackendSearchTerm={setSearchTerm}
      loadingOptions={projectsQuery.query.loading || searchTerm !== debouncedSearchTerm}
      labelPosition="top"
      options={options}
      selectedOptionValues={selectedOptionsValues}
      setSelectedOptions={(options) => {
        setSelectedProject(projectList?.find((project) => project.id === options[0]?.value));
        setSelectedTagGroups([]);
      }}
    />
  );
};

export default ProjectAutocomplete;
